/**
 * Qualifying Life Event (QLE) Types and Utilities
 *
 * Defines all supported QLE types and their allowed benefit changes.
 * Used by both frontend wizard and backend validation.
 */

import {
  getFirstOfNextMonth,
  getFirstOfFollowingMonth,
  get15thOfMonth,
} from "./eligibilityCalculator";

/**
 * All supported QLE event types
 */
export enum QLEEventType {
  MARRIAGE = "marriage",
  DIVORCE = "divorce",
  BIRTH_ADOPTION = "birth_adoption",
  DEATH_OF_DEPENDENT = "death_of_dependent",
  LOSS_OF_COVERAGE = "loss_of_coverage",
  GAIN_OF_COVERAGE = "gain_of_coverage",
  SPOUSE_EMPLOYMENT_CHANGE = "spouse_employment_change",
  ADDRESS_CHANGE = "address_change",
  DEPENDENT_ELIGIBILITY_CHANGE = "dependent_eligibility_change",
  COURT_ORDER = "court_order",
  OTHER = "other",
}

/**
 * Actions that can be taken during a QLE
 */
export enum QLEAction {
  ADD_DEPENDENT = "add_dependent",
  REMOVE_DEPENDENT = "remove_dependent",
  CHANGE_PLAN = "change_plan",
  ENROLL = "enroll",
  WAIVE = "waive",
  INCREASE_COVERAGE = "increase_coverage",
  DECREASE_COVERAGE = "decrease_coverage",
}

/**
 * Documentation requirements for each event type
 */
export enum DocumentationType {
  REQUIRED = "required",
  RECOMMENDED = "recommended",
  OPTIONAL = "optional",
}

/**
 * QLE event definition with metadata
 */
export interface QLEEventDefinition {
  type: QLEEventType;
  label: string;
  description: string;
  icon: string; // Icon name to use in UI
  allowedActions: QLEAction[];
  documentationRequired: DocumentationType;
  documentationExamples: string[];
  deadlineDays: number; // Days from event date to make changes
}

/**
 * Complete definitions for all QLE event types
 */
export const QLE_EVENT_DEFINITIONS: Record<QLEEventType, QLEEventDefinition> = {
  [QLEEventType.MARRIAGE]: {
    type: QLEEventType.MARRIAGE,
    label: "Marriage / Domestic Partnership",
    description: "You got married or entered a domestic partnership",
    icon: "Heart",
    allowedActions: [
      QLEAction.ADD_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.INCREASE_COVERAGE,
    ],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: ["Marriage certificate", "Domestic partnership certificate"],
    deadlineDays: 30,
  },
  [QLEEventType.DIVORCE]: {
    type: QLEEventType.DIVORCE,
    label: "Divorce / Legal Separation",
    description: "Your marriage or domestic partnership ended",
    icon: "UserMinus",
    allowedActions: [
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.DECREASE_COVERAGE,
    ],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: ["Divorce decree", "Legal separation documents"],
    deadlineDays: 30,
  },
  [QLEEventType.BIRTH_ADOPTION]: {
    type: QLEEventType.BIRTH_ADOPTION,
    label: "Birth or Adoption of Child",
    description: "You had a baby or adopted a child",
    icon: "Baby",
    allowedActions: [
      QLEAction.ADD_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.INCREASE_COVERAGE,
      QLEAction.ENROLL,
    ],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: [
      "Birth certificate",
      "Hospital birth record",
      "Adoption papers",
      "Placement order",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.DEATH_OF_DEPENDENT]: {
    type: QLEEventType.DEATH_OF_DEPENDENT,
    label: "Death of Dependent",
    description: "A covered dependent has passed away",
    icon: "HeartOff",
    allowedActions: [
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.DECREASE_COVERAGE,
    ],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: ["Death certificate"],
    deadlineDays: 30,
  },
  [QLEEventType.LOSS_OF_COVERAGE]: {
    type: QLEEventType.LOSS_OF_COVERAGE,
    label: "Loss of Other Coverage",
    description: "You or a dependent lost health coverage from another source",
    icon: "ShieldOff",
    allowedActions: [
      QLEAction.ENROLL,
      QLEAction.ADD_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.INCREASE_COVERAGE,
    ],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: [
      "Letter from previous insurer",
      "COBRA notice",
      "Termination of coverage letter",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.GAIN_OF_COVERAGE]: {
    type: QLEEventType.GAIN_OF_COVERAGE,
    label: "Gain of Other Coverage",
    description: "You or a dependent gained health coverage from another source",
    icon: "ShieldCheck",
    allowedActions: [
      QLEAction.WAIVE,
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.DECREASE_COVERAGE,
    ],
    documentationRequired: DocumentationType.RECOMMENDED,
    documentationExamples: [
      "New insurance card",
      "Enrollment confirmation",
      "Coverage effective date letter",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.SPOUSE_EMPLOYMENT_CHANGE]: {
    type: QLEEventType.SPOUSE_EMPLOYMENT_CHANGE,
    label: "Spouse Employment Change",
    description: "Your spouse started or ended employment affecting their benefits",
    icon: "Briefcase",
    allowedActions: [
      QLEAction.ADD_DEPENDENT,
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.ENROLL,
      QLEAction.WAIVE,
    ],
    documentationRequired: DocumentationType.RECOMMENDED,
    documentationExamples: [
      "Spouse's new employment letter",
      "Spouse's termination letter",
      "Spouse's benefits enrollment/termination confirmation",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.ADDRESS_CHANGE]: {
    type: QLEEventType.ADDRESS_CHANGE,
    label: "Change of Address",
    description: "You moved to a new address that may affect plan availability",
    icon: "MapPin",
    allowedActions: [QLEAction.CHANGE_PLAN],
    documentationRequired: DocumentationType.OPTIONAL,
    documentationExamples: ["Utility bill", "Lease agreement", "Mortgage document"],
    deadlineDays: 30,
  },
  [QLEEventType.DEPENDENT_ELIGIBILITY_CHANGE]: {
    type: QLEEventType.DEPENDENT_ELIGIBILITY_CHANGE,
    label: "Dependent Eligibility Change",
    description: "A dependent gained or lost eligibility (e.g., child turning 26)",
    icon: "UserCog",
    allowedActions: [
      QLEAction.ADD_DEPENDENT,
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.CHANGE_PLAN,
    ],
    documentationRequired: DocumentationType.RECOMMENDED,
    documentationExamples: [
      "Student enrollment verification",
      "Court documents for custody",
      "Marriage certificate (dependent)",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.COURT_ORDER]: {
    type: QLEEventType.COURT_ORDER,
    label: "Court Order (QMCSO)",
    description: "A court order requires you to provide coverage for a dependent",
    icon: "Scale",
    allowedActions: [QLEAction.ADD_DEPENDENT, QLEAction.ENROLL],
    documentationRequired: DocumentationType.REQUIRED,
    documentationExamples: [
      "Qualified Medical Child Support Order (QMCSO)",
      "Court order documentation",
    ],
    deadlineDays: 30,
  },
  [QLEEventType.OTHER]: {
    type: QLEEventType.OTHER,
    label: "Other Qualifying Event",
    description: "Another event that may qualify for benefit changes",
    icon: "HelpCircle",
    allowedActions: [
      QLEAction.ADD_DEPENDENT,
      QLEAction.REMOVE_DEPENDENT,
      QLEAction.CHANGE_PLAN,
      QLEAction.ENROLL,
      QLEAction.WAIVE,
    ],
    documentationRequired: DocumentationType.RECOMMENDED,
    documentationExamples: ["Supporting documentation for your specific event"],
    deadlineDays: 30,
  },
};

/**
 * Get all QLE event types as options for a dropdown
 */
export function getQLEEventOptions(): Array<{ value: string; label: string; description: string }> {
  return Object.values(QLE_EVENT_DEFINITIONS).map((def) => ({
    value: def.type,
    label: def.label,
    description: def.description,
  }));
}

/**
 * Get the definition for a specific event type
 */
export function getQLEEventDefinition(eventType: QLEEventType): QLEEventDefinition | undefined {
  return QLE_EVENT_DEFINITIONS[eventType];
}

/**
 * Calculate the QLE enrollment effective date based on the plan's entry timing.
 *
 * When `entryDate` is provided, applies the plan's entry timing to the event date.
 * When omitted, defaults to `first_of_following_month` (original behavior).
 */
export function calculateQLEEffectiveDate(eventDate: Date, entryDate?: string): Date {
  // Normalize entryDate to underscores — DB values may contain dashes
  const normalized = String(entryDate || "first_of_following_month").replace(/-/g, "_");

  switch (normalized) {
    case "immediate":
      return new Date(eventDate);
    case "first_of_month":
      return eventDate.getUTCDate() === 1
        ? new Date(eventDate)
        : getFirstOfNextMonth(eventDate);
    case "15th_of_month":
      return get15thOfMonth(eventDate);
    case "first_of_following_month":
    default:
      return getFirstOfFollowingMonth(eventDate);
  }
}

/**
 * Calculate the deadline date for a QLE
 */
export function calculateQLEDeadline(eventDate: Date, eventType: QLEEventType): Date {
  const definition = QLE_EVENT_DEFINITIONS[eventType];
  const deadline = new Date(eventDate);
  deadline.setDate(deadline.getDate() + (definition?.deadlineDays || 30));
  return deadline;
}

/**
 * Check if a QLE is still within the change window
 */
export function isQLEWithinDeadline(eventDate: Date, eventType: QLEEventType): boolean {
  const deadline = calculateQLEDeadline(eventDate, eventType);
  return new Date() <= deadline;
}

// ─────────────────────────────────────────────────────────────────────────────
// Event-type → effective-date rules
//
// Distinct from calculateQLEEffectiveDate above, which maps a plan's entry-timing
// SETTING (immediate / first_of_month / 15th_of_month / first_of_following_month)
// to an effective date. The rules below map a QLE EVENT TYPE (marriage, new_hire,
// etc.) to a default effective date for that event. Used by the per-employee
// benefits Modify modal so brokers don't have to compute it manually.
// ─────────────────────────────────────────────────────────────────────────────

export type QLEEffectiveRule = "event_date" | "first_of_month_after";

export interface QLEEventRule {
  value: string;
  label: string;
  effectiveRule: QLEEffectiveRule;
  /**
   * Days to add to the event date BEFORE computing first-of-next-month
   * (used by `first_of_month_after`). Ignored when `effectiveRule = "event_date"`.
   */
  defaultDays: number;
}

export const QLE_EVENT_RULES: QLEEventRule[] = [
  { value: "administrative_correction", label: "Administrative correction", effectiveRule: "event_date",           defaultDays: 0 },
  { value: "new_hire",                  label: "New hire",                  effectiveRule: "first_of_month_after", defaultDays: 30 },
  { value: "marriage",                  label: "Marriage",                  effectiveRule: "first_of_month_after", defaultDays: 30 },
  { value: "birth",                     label: "Birth / adoption",          effectiveRule: "event_date",           defaultDays: 0 },
  { value: "loss_of_coverage",          label: "Loss of other coverage",    effectiveRule: "first_of_month_after", defaultDays: 0 },
  { value: "divorce",                   label: "Divorce / legal separation",effectiveRule: "event_date",           defaultDays: 0 },
  { value: "open_enrollment",           label: "Open enrollment",           effectiveRule: "event_date",           defaultDays: 0 },
  { value: "broker_override",           label: "Broker override",           effectiveRule: "event_date",           defaultDays: 0 },
];

/**
 * Given a QLE event type (one of `QLE_EVENT_RULES[i].value`) and an ISO-date
 * event date (YYYY-MM-DD), return the calculated effective-date string.
 * Falls back to the event date unchanged for unknown event types.
 */
export function calculateEffectiveDateFromQLE(eventType: string, eventDate: string): string {
  if (!eventDate) return "";
  const rule = QLE_EVENT_RULES.find((r) => r.value === eventType) ?? QLE_EVENT_RULES[0];
  const d = new Date(eventDate + "T00:00:00");
  if (Number.isNaN(d.getTime())) return eventDate;
  if (rule.effectiveRule === "event_date") return eventDate;
  // first_of_month_after — add the per-event offset, then jump to first of next month.
  if (rule.defaultDays > 0) d.setDate(d.getDate() + rule.defaultDays);
  const firstOfNext = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return firstOfNext.toISOString().split("T")[0];
}

/**
 * Get the number of days remaining for a QLE
 */
export function getQLEDaysRemaining(eventDate: Date, eventType: QLEEventType): number {
  const deadline = calculateQLEDeadline(eventDate, eventType);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deadline.setHours(0, 0, 0, 0);
  const diffTime = deadline.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Get human-readable action labels
 */
export const QLE_ACTION_LABELS: Record<QLEAction, string> = {
  [QLEAction.ADD_DEPENDENT]: "Add dependent(s) to coverage",
  [QLEAction.REMOVE_DEPENDENT]: "Remove dependent(s) from coverage",
  [QLEAction.CHANGE_PLAN]: "Change your plan selection",
  [QLEAction.ENROLL]: "Enroll in benefits",
  [QLEAction.WAIVE]: "Waive coverage",
  [QLEAction.INCREASE_COVERAGE]: "Increase coverage amount",
  [QLEAction.DECREASE_COVERAGE]: "Decrease coverage amount",
};
